import React, { useState, useEffect, useMemo } from 'react'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import SidePanel from '@/components/ui/SidePanel'
import { designTokens } from '@/utils/design-tokens'
import { PerformanceService } from '@/api/generated/services/PerformanceService'
import { DepartmentService } from '@/api/generated/services/DepartmentService'
import { OrgMemberService } from '@/api/generated/services/OrgMemberService'
import { useAppSelector } from '@/store/hooks'
import DepartmentSelect from '@/components/ui/DepartmentSelect'
import MemberSelect from '@/components/ui/MemberSelect'
import type { PerformanceFullCreateRequest } from '@/api/generated/models/PerformanceFullCreateRequest'
import type { DepartmentResponse } from '@/api/generated/models/DepartmentResponse'
import type { OrgMemberResponse } from '@/api/generated/models/OrgMemberResponse'
import type { PaginatedResponse } from '@/api/generated/models/PaginatedResponse'
import RequiredLabel from '@/components/ui/RequiredLabel'

interface PerformanceItem {
  id: string
  name: string
  target: number | null
  achievement: number | null
}

interface PerformanceCreatePanelProps {
  isOpen: boolean
  onClose: () => void
  parentClassId: string
  parentClassName: string
  onSuccess?: () => void
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div 
    className="pb-2 border-b" 
    style={{ 
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3 
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{ 
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean 
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span 
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span 
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const PerformanceCreatePanel: React.FC<PerformanceCreatePanelProps> = ({
  isOpen,
  onClose,
  parentClassId,
  parentClassName,
  onSuccess
}) => {
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  
  // Form state
  const [performanceName, setPerformanceName] = useState('')
  const [unit, setUnit] = useState('')
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null)
  const [selectedDepartmentName, setSelectedDepartmentName] = useState<string | null>(null)
  const [assigneeId, setAssigneeId] = useState<string | null>(null)
  const [assigneeName, setAssigneeName] = useState<string | null>(null)
  const [performanceItems, setPerformanceItems] = useState<PerformanceItem[]>([])
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [departmentError, setDepartmentError] = useState<string | null>(null)

  // Data loading state
  const [departments, setDepartments] = useState<DepartmentResponse[]>([])
  const [isDepartmentsLoading, setIsDepartmentsLoading] = useState(false)
  const [departmentsError, setDepartmentsError] = useState<string | null>(null)

  const [workspaceMembers, setWorkspaceMembers] = useState<OrgMemberResponse[]>([])
  const [isMembersLoading, setIsMembersLoading] = useState(false)
  const [membersError, setMembersError] = useState<string | null>(null)

  // Reset form when panel opens/closes
  useEffect(() => {
    if (isOpen) {
      setPerformanceName('')
      setUnit('')
      setSelectedDepartmentId(null)
      setSelectedDepartmentName(null)
      setAssigneeId(null)
      setAssigneeName(null)
      setPerformanceItems([])
      setError(null)
      setDepartmentError(null)
    }
  }, [isOpen])

  // Fetch departments when workspace changes
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!selectedWorkspace?.id) return
      
      setIsDepartmentsLoading(true)
      setDepartmentsError(null)
      
      try {
        const response: PaginatedResponse = await DepartmentService.appsDepartmentViewsListDepartments({
          workspaceId: selectedWorkspace.id,
          page: 1,
          perPage: 100
        })
        
        setDepartments(response.data as DepartmentResponse[] || [])
      } catch (err) {
        console.error('Failed to fetch departments:', err)
        setDepartmentsError('부서 목록을 불러오는데 실패했습니다.')
      } finally {
        setIsDepartmentsLoading(false)
      }
    }
    
    fetchDepartments()
  }, [selectedWorkspace?.id])

  // Fetch workspace members when workspace changes
  useEffect(() => {
    const fetchWorkspaceMembers = async () => {
      if (!selectedWorkspace?.id) return
      
      setIsMembersLoading(true)
      setMembersError(null)
      
      try {
        const response: PaginatedResponse = await OrgMemberService.appsOrganizationViewsListOrgMembers({
          orgId: selectedWorkspace.org_id,
          workspaceId: selectedWorkspace.id,
          page: 1,
          perPage: 1000
        })
        
        // Transform OrgMemberResponse to WorkspaceMemberResponse format
        const orgMembers = response.data as OrgMemberResponse[] || []
        const workspaceMembers: OrgMemberResponse[] = orgMembers
          .filter(member => member.workspaces?.some(ws => ws.id === selectedWorkspace.id))
          .map(member => ({
            id: member.id,
            account_id: member.account_id,
            account_name: member.account_name,
            account_email: member.account_email,
            account_phone: member.account_phone, // Not available in OrgMemberResponse
            workspace_id: selectedWorkspace.id,
            workspace_name: member.workspaces?.find(ws => ws.id === selectedWorkspace.id)?.name || ''
          }))
        
        setWorkspaceMembers(workspaceMembers)
      } catch (err) {
        console.error('Failed to fetch workspace members:', err)
        setMembersError('멤버 목록을 불러오는데 실패했습니다.')
      } finally {
        setIsMembersLoading(false)
      }
    }
    
    fetchWorkspaceMembers()
  }, [selectedWorkspace?.id, selectedWorkspace?.org_id])

  // Calculate totals
  const totals = useMemo(() => {
    const totalTarget = performanceItems.reduce((sum, item) => sum + (item.target || 0), 0)
    const totalAchievement = performanceItems.reduce((sum, item) => sum + (item.achievement || 0), 0)
    const achievementRate = totalTarget > 0 ? Math.round((totalAchievement / totalTarget) * 100) : 0

    return {
      target: totalTarget,
      achievement: totalAchievement,
      rate: achievementRate
    }
  }, [performanceItems])

  // Format number with commas
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('ko-KR').format(num)
  }

  // Handle number input parsing
  const parseNumberInput = (value: string): number | null => {
    const cleanValue = value.replace(/,/g, '').trim()
    if (!cleanValue) return null
    const num = parseFloat(cleanValue)
    return isNaN(num) ? null : num
  }

  // Handle adding new performance item
  const handleAddItem = () => {
    const newItem: PerformanceItem = {
      id: `perf-item-${Date.now()}`,
      name: '',
      target: null,
      achievement: null
    }
    setPerformanceItems([...performanceItems, newItem])
  }

  // Handle removing performance item
  const handleRemoveItem = (itemId: string) => {
    setPerformanceItems(performanceItems.filter(item => item.id !== itemId))
  }

  // Handle item field changes
  const handleItemChange = (itemId: string, field: keyof PerformanceItem, value: string | number | null) => {
    const updatedItems = performanceItems.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    )
    setPerformanceItems(updatedItems)
  }

  // Department selection handler
  const handleDepartmentSelect = (departmentId: string, departmentName: string) => {
    setSelectedDepartmentId(departmentId)
    setSelectedDepartmentName(departmentName)
    setDepartmentError(null)
  }

  // Assignee selection handler
  const handleAssigneeSelect = (memberId: string, memberName: string, _memberEmail: string) => {
    setAssigneeId(memberId || null)
    setAssigneeName(memberName || null)
  }

  const handleSubmit = async () => {
    if (!selectedWorkspace?.id) {
      setError('워크스페이스가 선택되지 않았습니다.')
      return
    }

    if (!performanceName.trim()) {
      setError('세부지표명을 입력해주세요.')
      return
    }

    if (!unit.trim()) {
      setError('단위를 입력해주세요.')
      return
    }

    if (!selectedDepartmentId) {
      setDepartmentError('부서를 선택해주세요.')
      return
    }

    if (!assigneeId) {
      setError('담당자를 선택해주세요.')
      return
    }

    if (performanceItems.length === 0) {
      setError('최소 1개의 세부지표 항목을 추가해주세요.')
      return
    }

    // Validate performance items
    for (const item of performanceItems) {
      if (!item.name.trim()) {
        setError('모든 세부지표 항목의 이름을 입력해주세요.')
        return
      }
      if (item.target === null || item.target < 0) {
        setError('모든 세부지표 항목의 목표 수치를 입력해주세요.')
        return
      }
      if (item.achievement === null || item.achievement < 0) {
        setError('모든 세부지표 항목의 달성 수치를 입력해주세요.')
        return
      }
    }

    setIsSubmitting(true)
    setError(null)
    setDepartmentError(null)

    try {
      const createRequest: PerformanceFullCreateRequest = {
        parent_class_id: parentClassId,
        assignee_id: assigneeId,
        department_id: selectedDepartmentId,
        name: performanceName.trim(),
        unit: unit.trim(),
        items: performanceItems.map(item => ({
          name: item.name.trim(),
          target_figure: item.target || 0,
          done_figure: item.achievement || 0
        }))
      }

      await PerformanceService.appsPerformanceViewsCreatePerformanceFull({
        requestBody: createRequest
      })

      // Success
      onSuccess?.()
      onClose()
    } catch (err) {
      console.error('Failed to create performance:', err)
      
      if (err && typeof err === 'object' && 'status' in err) {
        switch (err.status) {
          case 400:
            setError('입력 데이터가 올바르지 않습니다.')
            break
          case 401:
            setError('인증이 만료되었습니다. 다시 로그인해주세요.')
            break
          case 404:
            setError('상위 분류를 찾을 수 없습니다.')
            break
          case 409:
            setError('이미 존재하는 세부지표명입니다.')
            break
          case 422:
            setError('입력 데이터의 형식이 올바르지 않습니다.')
            break
          case 500:
            setError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
            break
          default:
            setError('성과 생성에 실패했습니다. 다시 시도해주세요.')
        }
      } else {
        setError('성과 생성에 실패했습니다. 다시 시도해주세요.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (isSubmitting) return
    onClose()
  }

  const isFormValid = performanceName.trim() && unit.trim() && selectedDepartmentId && assigneeId && performanceItems.length > 0

  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={handleCancel}
        disabled={isSubmitting}
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSubmit}
        disabled={isSubmitting || !isFormValid}
      >
        {isSubmitting ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={handleCancel}
      title="신규 세부지표 생성"
      width={800}
      headerActions={headerActions}
    >
      <div 
        className="flex flex-col space-y-6" 
        style={{ 
          padding: '24px',
        }}
      >
        {/* Error Display */}
        {error && (
          <div 
            className="p-3 rounded border"
            style={{
              backgroundColor: '#FFF5F5',
              borderColor: designTokens.colors.error.DEFAULT,
              color: designTokens.colors.error.DEFAULT,
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px'
            }}
          >
            {error}
          </div>
        )}

        {/* Section 1: 기본정보 */}
        <div className="space-y-4">
          <SectionHeader title="기본정보" />
          <div className="space-y-4" style={{ padding: '0px 4px' }}>
            <InfoField 
              label="유형" 
              value={
                <Tag color="info" size="small">
                  ● 성과
                </Tag>
              }
            />
            <InfoField
              label="상위 성과지표"
              value={parentClassName}
            />
            
            <InfoField 
              label={<RequiredLabel>세부지표명</RequiredLabel>}
              fullWidth={true}
              value={
                <input
                  type="text"
                  value={performanceName}
                  onChange={(e) => setPerformanceName(e.target.value)}
                  placeholder="세부지표명을 입력하세요"
                  className="w-full p-2 border rounded"
                  style={{
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                  disabled={isSubmitting}
                />
              }
            />

            <InfoField 
              label={<RequiredLabel>단위</RequiredLabel>}
              fullWidth={true}
              value={
                <input
                  type="text"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  placeholder="단위를 입력하세요 (예: 명, 건, %)"
                  className="w-full p-2 border rounded"
                  style={{
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                  disabled={isSubmitting}
                />
              }
            />

            <InfoField 
              label={<RequiredLabel>부서</RequiredLabel>}
              value={
                <DepartmentSelect
                  selectedDepartmentId={selectedDepartmentId}
                  selectedDepartmentName={selectedDepartmentName}
                  onSelect={handleDepartmentSelect}
                  disabled={isSubmitting}
                  error={departmentError}
                  placeholder="부서를 선택하세요"
                  workspaceId={selectedWorkspace?.id || ''}
                  departments={departments}
                  isLoading={isDepartmentsLoading}
                  departmentsError={departmentsError}
                />
              }
              fullWidth
            />

            <InfoField 
              label={<RequiredLabel>담당자</RequiredLabel>}
              value={
                <MemberSelect
                  selectedMemberId={assigneeId}
                  selectedMemberName={assigneeName}
                  onSelect={handleAssigneeSelect}
                  disabled={isSubmitting}
                  placeholder="담당자를 선택하세요"
                  workspaceId={selectedWorkspace?.id || ''}
                  members={workspaceMembers}
                  isLoading={isMembersLoading}
                  membersError={membersError}
                />
              }
              fullWidth
            />
          </div>
        </div>

        {/* Section 2: 세부지표 항목 */}
        <div className="space-y-4">
          <SectionHeader title="세부지표 항목" />
          <div className="space-y-4" style={{ padding: '0px 4px' }}>
            {/* Performance Summary and Add Button */}
            <div className="space-y-3" style={{ padding: '4px 0px' }}>
              <div className="flex justify-center items-center gap-2.5">
                {/* Total Performance Label */}
                <div className="flex justify-center" style={{ width: '160px' }}>
                  <span 
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    전체 성과
                  </span>
                </div>

                {/* Performance Summary */}
                <div className="flex justify-center gap-2.5" style={{ flex: 1 }}>
                  {/* Target */}
                  <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                    <div style={{ width: '120px' }}>
                      <span 
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '14px',
                          fontWeight: 400,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        목표
                      </span>
                    </div>
                    <div className="flex items-center p-3" style={{ width: '100%', height: 'auto' }}>
                      <span 
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '16px',
                          fontWeight: 400,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        {formatNumber(totals.target)}
                      </span>
                    </div>
                  </div>

                  {/* Achievement */}
                  <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                    <div style={{ width: '120px' }}>
                      <span 
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '14px',
                          fontWeight: 400,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        달성
                      </span>
                    </div>
                    <div className="flex items-center p-3" style={{ width: '100%', height: 'auto' }}>
                      <span 
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '16px',
                          fontWeight: 400,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        {formatNumber(totals.achievement)}
                      </span>
                    </div>
                  </div>

                  {/* Achievement Rate */}
                  <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                    <div style={{ width: '120px' }}>
                      <span 
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '14px',
                          fontWeight: 400,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        달성율
                      </span>
                    </div>
                    <div className="flex items-center p-3" style={{ width: '100%', height: 'auto' }}>
                      <span 
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '16px',
                          fontWeight: 400,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        {totals.rate}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Add Button */}
                <div className="flex justify-center items-center gap-1 px-2 py-0" style={{ width: 'auto', height: '28px' }}>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    disabled={isSubmitting}
                    className="flex items-center gap-1 px-2 py-0 rounded transition-colors"
                    style={{
                      backgroundColor: isSubmitting ? designTokens.colors.gray[1] : designTokens.colors.gray[1],
                      color: isSubmitting ? designTokens.colors.gray[3] : designTokens.colors.gray[6],
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      fontWeight: 600,
                      border: `2px solid ${designTokens.colors.gray[1]}`,
                      borderRadius: '4px',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      height: '28px'
                    }}
                  >
                    <span style={{ fontSize: '14px', lineHeight: '14px' }}>+</span>
                    항목 추가
                  </button>
                </div>
              </div>

              {/* Performance Items */}
              {performanceItems.length > 0 && (
                <div className="space-y-1.5">
                  {performanceItems.map((item) => (
                    <div key={item.id} className="flex justify-center items-center gap-2.5">
                      {/* Tree Indicator and Performance Name */}
                      <div className="flex items-center gap-1.5 px-0 py-0" style={{ paddingLeft: '10px', width: 'auto' }}>
                        <span 
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '14px',
                            fontWeight: 400,
                            color: designTokens.colors.gray[6]
                          }}
                        >
                          ㄴ
                        </span>
                        <div className="flex flex-col justify-center gap-2" style={{ width: '150px' }}>
                          <div style={{ width: '120px' }}>
                            <div className="flex items-center gap-0.5">
                              <span 
                                style={{
                                  fontFamily: designTokens.typography.fontFamily.sans[0],
                                  fontSize: '12px',
                                  fontWeight: 400,
                                  color: designTokens.colors.gray[6]
                                }}
                              >
                                항목명
                              </span>
                              <span style={{ color: designTokens.colors.error.DEFAULT }}>*</span>
                            </div>
                          </div>
                          <div className="flex flex-col justify-center gap-2" style={{ width: '100%' }}>
                            <div className="flex items-center gap-1.5 px-3 py-0 border rounded" style={{ width: '100%', height: '32px', backgroundColor: isSubmitting ? designTokens.colors.gray[0] : 'white', borderColor: designTokens.colors.gray[1] }}>
                              <input
                                type="text"
                                value={item.name}
                                onChange={(e) => handleItemChange(item.id, 'name', e.target.value)}
                                placeholder="2024년 상반기"
                                disabled={isSubmitting}
                                className="w-full border-none outline-none bg-transparent"
                                style={{
                                  fontFamily: designTokens.typography.fontFamily.sans[0],
                                  fontSize: '14px',
                                  color: designTokens.colors.gray[6]
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Target */}
                      <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                        <div style={{ width: '120px' }}>
                          <div className="flex items-center gap-0.5">
                            <span 
                              style={{
                                fontFamily: designTokens.typography.fontFamily.sans[0],
                                fontSize: '12px',
                                fontWeight: 400,
                                color: designTokens.colors.gray[6]
                              }}
                            >
                              목표
                            </span>
                            <span style={{ color: designTokens.colors.error.DEFAULT }}>*</span>
                          </div>
                        </div>
                        <div className="flex flex-col justify-center gap-2" style={{ width: '100%' }}>
                          <div className="flex items-center gap-1.5 px-3 py-0 border rounded" style={{ width: '100%', height: '32px', backgroundColor: isSubmitting ? designTokens.colors.gray[0] : 'white', borderColor: designTokens.colors.gray[1] }}>
                            <input
                              type="text"
                              value={item.target ? formatNumber(item.target) : ''}
                              onChange={(e) => handleItemChange(item.id, 'target', parseNumberInput(e.target.value))}
                              placeholder="150"
                              disabled={isSubmitting}
                              className="w-full border-none outline-none bg-transparent"
                              style={{
                                fontFamily: designTokens.typography.fontFamily.sans[0],
                                fontSize: '14px',
                                color: designTokens.colors.gray[6]
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Achievement */}
                      <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                        <div style={{ width: '120px' }}>
                          <div className="flex items-center gap-0.5">
                            <span 
                              style={{
                                fontFamily: designTokens.typography.fontFamily.sans[0],
                                fontSize: '12px',
                                fontWeight: 400,
                                color: designTokens.colors.gray[6]
                              }}
                            >
                              달성
                            </span>
                            <span style={{ color: designTokens.colors.error.DEFAULT }}>*</span>
                          </div>
                        </div>
                        <div className="flex flex-col justify-center gap-2" style={{ width: '100%' }}>
                          <div className="flex items-center gap-1.5 px-3 py-0 border rounded" style={{ width: '100%', height: '32px', backgroundColor: isSubmitting ? designTokens.colors.gray[0] : 'white', borderColor: designTokens.colors.gray[1] }}>
                            <input
                              type="text"
                              value={item.achievement !== null && item.achievement !== undefined ? formatNumber(item.achievement) : '0'}
                              onChange={(e) => handleItemChange(item.id, 'achievement', parseNumberInput(e.target.value))}
                              onBlur={(e) => {
                                // If empty or null, set to 0
                                if (e.target.value === '' || parseNumberInput(e.target.value) === null) {
                                  handleItemChange(item.id, 'achievement', 0)
                                }
                              }}
                              placeholder="50"
                              disabled={isSubmitting}
                              className="w-full border-none outline-none bg-transparent"
                              style={{
                                fontFamily: designTokens.typography.fontFamily.sans[0],
                                fontSize: '14px',
                                color: designTokens.colors.gray[6]
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Delete Button */}
                      <div className="flex flex-col justify-end items-center" style={{ width: 'auto', height: '100%' }}>
                        <div className="flex justify-center items-center gap-1 px-2.5 py-0 border rounded transition-colors" style={{ width: 'auto', height: '34px', backgroundColor: isSubmitting ? designTokens.colors.gray[1] : designTokens.colors.gray[1], borderColor: designTokens.colors.gray[1], cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(item.id)}
                            disabled={isSubmitting}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: isSubmitting ? designTokens.colors.gray[3] : designTokens.colors.gray[6],
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: '16px',
                              fontWeight: 600,
                              cursor: isSubmitting ? 'not-allowed' : 'pointer'
                            }}
                          >
                            삭제
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SidePanel>
  )
}

export default PerformanceCreatePanel